import { useState, useEffect, useCallback } from 'react'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Database, Search, TrendingUp, Table as TableIcon, RefreshCw, ChevronRight, FolderOpen, Zap, ArrowRight, Sparkles, Download, Loader2, Star } from 'lucide-react'
import { BUSINESS_TOPICS } from '@/data/topics'
import { CATEGORIES } from '@/data/shared-templates'
import { getTopicQueries, getAiQueryResult, type AiQueryResult } from '@/data/topics-ai-queries'
import { cn } from '@/lib/utils'

/** 图表可视化组件 */
function ChartVisualization({ result }: { result: AiQueryResult }) {
  const rows = result.rows
  const valueCol = result.columns.find(c =>
    c.key === 'amount' || c.key === 'totalAmount' || c.key === 'balance' || c.key === 'count' || c.key === 'creditAmount' || c.key === 'npl' || c.key === 'dailyAvg' || c.key === 'avgTime' || c.key === 'change'
  )?.key || result.columns[1]?.key
  const labelCol = result.columns[0]?.key
  const values = rows.map(r => {
    const v = r[valueCol]
    return parseFloat(String(v).replace(/[¥,亿元%+\-—,]/g, '')) || 0
  })
  const maxVal = Math.max(...values, 1)
  const chartH = 120
  const points = values.map((v, i) => ({
    x: ((i + 0.5) / rows.length) * 100,
    y: chartH - (v / maxVal) * chartH * 0.85,
  }))
  const path = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ')
  const barWidth = Math.max(60 / rows.length, 6)

  return (
    <div className="p-4 bg-white">
      <div className="relative h-36">
        <svg viewBox={`0 0 100 ${chartH}`} className="w-full h-full" preserveAspectRatio="none">
          {[0, 0.25, 0.5, 0.75, 1].map((pct, i) => (
            <line key={i} x1="0" x2="100" y1={chartH - chartH * pct * 0.85} y2={chartH - chartH * pct * 0.85} stroke="#e5e7eb" strokeWidth="0.2" vectorEffect="non-scaling-stroke" />
          ))}
          {values.map((v, i) => {
            const barH = (v / maxVal) * chartH * 0.85
            const x = ((i + 0.5) / rows.length) * 100 - barWidth / 2
            return (
              <rect key={i} x={x} y={chartH - barH} width={barWidth} height={barH} fill="#c4b5fd" rx="0.5" />
            )
          })}
          <path d={path} fill="none" stroke="#7c3aed" strokeWidth="0.8" vectorEffect="non-scaling-stroke" />
          {points.map((p, i) => (
            <circle key={i} cx={p.x} cy={p.y} r="1.2" fill="#7c3aed" stroke="#fff" strokeWidth="0.5" />
          ))}
        </svg>
        <div className="absolute bottom-0 inset-x-0 flex">
          {rows.map((r, i) => (
            <div key={i} className="flex-1 flex justify-center">
              <span className="text-[9px] text-gray-400 truncate px-0.5">{String(r[labelCol])}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

interface TopicsPageProps {
  onLearnMore: (catalogId: string) => void
  onSelfAnalysis?: (topicId: string) => void
  /** When true, the page initializes with favorites filter active (used by MySpace navigation) */
  showFavoritesOnly?: boolean
  version?: string
}

export function TopicsPage({ onLearnMore, onSelfAnalysis, showFavoritesOnly: initialShowFavorites, version }: TopicsPageProps) {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [selectedSubCategory, setSelectedSubCategory] = useState<string | null>(null)
  const [searchKeyword, setSearchKeyword] = useState('')
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(initialShowFavorites ?? false)

  // 零售业务子目录
  const RETAIL_SUB_CATEGORIES = ['银行卡', '投资理财', '个人存款', '个人贷款', '电子银行', '支付结算', '中间业务', '信用卡', '其他']
  // 对公业务子目录
  const CORP_SUB_CATEGORIES = ['存款产品', '公司理财', '结算与现金管理', '供应链金融', '绿色金融', '跨境金融', '政府金融', '广企通', '普惠金融', '科技金融', '其他']
  // 金融市场子目录
  const MARKET_SUB_CATEGORIES = ['金融市场业务', '同业业务', '资产管理', '投资业务', '其他']

  /* ---- 收藏功能 ---- */
  const [favorites, setFavorites] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem('topic_favorites')
      if (saved) {
        const parsed = JSON.parse(saved)
        if (Array.isArray(parsed)) return new Set(parsed)
      }
    } catch {
      // ignore
    }
    return new Set<string>()
  })

  useEffect(() => {
    localStorage.setItem('topic_favorites', JSON.stringify([...favorites]))
  }, [favorites])

  const toggleFavorite = useCallback((id: string) => {
    setFavorites(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  /* ---- AI问数 ---- */
  const [showAiDialog, setShowAiDialog] = useState(false)
  const [aiTopicId, setAiTopicId] = useState<string>('')
  const [aiTopicName, setAiTopicName] = useState<string>('')
  const [aiQueryInput, setAiQueryInput] = useState('')
  const [aiLoading, setAiLoading] = useState(false)
  const [aiHistory, setAiHistory] = useState<AiQueryResult[]>([])
  const [aiSelectedQueryId, setAiSelectedQueryId] = useState<string | null>(null)
  const openAiDialog = (topicId: string) => {
    const topic = BUSINESS_TOPICS.find(t => t.id === topicId)
    if (!topic) return
    setAiTopicId(topicId)
    setAiTopicName(topic.name)
    setAiQueryInput('')
    setAiHistory([])
    setAiSelectedQueryId(null)
    setShowAiDialog(true)
  }

  const handleSelectQuery = (queryId: string, queryText: string) => {
    setAiSelectedQueryId(queryId)
    setAiQueryInput(queryText)
    runAiQuery(queryId)
  }

  const runAiQuery = (queryId?: string) => {
    if (!queryId) {
      // 自定义输入：尝试匹配已有推荐或返回一个通用结果
      setAiSelectedQueryId(null)
      setAiLoading(true)
      setTimeout(() => {
        const fallback: AiQueryResult = {
          query: aiQueryInput,
          resultType: 'table',
          title: aiQueryInput,
          summary: `关于"${aiQueryInput}"的分析结果如下：`,
          columns: [
            { key: 'dim', label: '维度' },
            { key: 'value', label: '数值' },
          ],
          rows: [
            { dim: '指标A', value: '1,256' },
            { dim: '指标B', value: '8,520' },
            { dim: '指标C', value: '3,680' },
          ],
        }
        setAiHistory(prev => [...prev, fallback])
        setAiLoading(false)
      }, 1200)
      return
    }
    setAiLoading(true)
    setTimeout(() => {
      const result = getAiQueryResult(queryId)
      if (result) {
        setAiHistory(prev => [...prev, result])
      }
      setAiLoading(false)
    }, 1200)
  }

  const handleDownloadResult = (result: AiQueryResult) => {
    const header = result.columns.map(c => c.label).join('\t')
    const rows = result.rows.map(r =>
      result.columns.map(c => r[c.key] ?? '').join('\t')
    ).join('\n')
    const content = `${result.title}\n${result.summary || ''}\n\n${header}\n${rows}`
    const blob = new Blob([content], { type: 'text/tab-separated-values;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${result.title}.tsv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const filteredTopics = BUSINESS_TOPICS.filter(topic => {
    if (showFavoritesOnly && !favorites.has(topic.id)) return false
    if (selectedCategory && topic.category !== selectedCategory) return false
    if (selectedCategory === '零售业务' && selectedSubCategory) {
      if (topic.subCategory !== selectedSubCategory) return false
    }
    if (selectedCategory === '对公业务' && selectedSubCategory) {
      if (topic.subCategory !== selectedSubCategory) return false
    }
    if (selectedCategory === '金融市场' && selectedSubCategory) {
      if (topic.subCategory !== selectedSubCategory) return false
    }
    if (searchKeyword.trim()) {
      const kw = searchKeyword.toLowerCase()
      return (
        topic.name.toLowerCase().includes(kw) ||
        topic.description.toLowerCase().includes(kw) ||
        topic.category.toLowerCase().includes(kw) ||
        topic.department.toLowerCase().includes(kw)
      )
    }
    return true
  })

  const categoryCounts: Record<string, number> = {}
  for (const cat of CATEGORIES) {
    categoryCounts[cat] = BUSINESS_TOPICS.filter(t => t.category === cat).length
  }

  const subCategoryCounts: Record<string, number> = {}
  if (selectedCategory === '零售业务') {
    for (const sub of RETAIL_SUB_CATEGORIES) {
      subCategoryCounts[sub] = BUSINESS_TOPICS.filter(t => t.category === '零售业务' && t.subCategory === sub).length
    }
  }
  if (selectedCategory === '对公业务') {
    for (const sub of CORP_SUB_CATEGORIES) {
      subCategoryCounts[sub] = BUSINESS_TOPICS.filter(t => t.category === '对公业务' && t.subCategory === sub).length
    }
  }
  if (selectedCategory === '金融市场') {
    for (const sub of MARKET_SUB_CATEGORIES) {
      subCategoryCounts[sub] = BUSINESS_TOPICS.filter(t => t.category === '金融市场' && t.subCategory === sub).length
    }
  }

  const displayTopics = selectedCategory
    ? filteredTopics
    : filteredTopics

  return (
    <div className="flex-1 overflow-auto bg-gray-50">
      <div className="p-6">
        <div className="flex gap-4">
          {/* 左侧业务目录 */}
          <div className="w-52 bg-white rounded-lg border shadow-sm overflow-hidden shrink-0">
            <div className="h-10 px-3 border-b bg-gray-50 flex items-center">
              <FolderOpen className="w-4 h-4 text-gray-600 mr-2" />
              <span className="text-xs font-semibold text-gray-700">业务目录</span>
            </div>
            <ScrollArea className="h-[500px]">
              <div className="p-2 space-y-1">
                <button
                  onClick={() => { setSelectedCategory(null); setShowFavoritesOnly(false) }}
                  className={`w-full text-left px-3 py-2 rounded-md text-xs transition-colors flex items-center gap-2 ${
                    selectedCategory === null && !showFavoritesOnly
                      ? 'bg-blue-50 text-gcb-primary font-medium'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <Database className="w-3.5 h-3.5" />
                  <span className="flex-1 truncate">全部主题</span>
                  <span className="text-[10px] text-gray-400">{BUSINESS_TOPICS.length}</span>
                </button>
                <button
                  onClick={() => { setShowFavoritesOnly(!showFavoritesOnly); setSelectedCategory(null) }}
                  className={`w-full text-left px-3 py-2 rounded-md text-xs transition-colors flex items-center gap-2 ${
                    showFavoritesOnly
                      ? 'bg-blue-50 text-gcb-primary font-medium'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <Star className={`w-3.5 h-3.5 ${showFavoritesOnly ? 'fill-yellow-400 text-yellow-400' : 'text-gray-400'}`} />
                  <span className="flex-1 truncate">我收藏的</span>
                  <span className="text-[10px] text-gray-400">{favorites.size}</span>
                </button>
                <div className="mt-2">
                  {CATEGORIES.map(cat => {
                    const count = categoryCounts[cat]
                    if (count === 0) return null
                    const isActive = selectedCategory === cat
                    const hasSubCategories = cat === '零售业务' || cat === '对公业务' || cat === '金融市场'
                    const expanded = hasSubCategories && isActive
                    const subCats = cat === '零售业务' ? RETAIL_SUB_CATEGORIES : cat === '对公业务' ? CORP_SUB_CATEGORIES : cat === '金融市场' ? MARKET_SUB_CATEGORIES : null
                    return (
                      <div key={cat}>
                        <button
                          onClick={() => { setSelectedCategory(isActive ? null : cat); setShowFavoritesOnly(false); setSelectedSubCategory(null) }}
                          className={`w-full text-left px-3 py-2 rounded-md text-xs transition-colors flex items-center gap-2 ${
                            isActive && !selectedSubCategory
                              ? 'bg-blue-50 text-gcb-primary font-medium'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          <ChevronRight className={`w-3 h-3 shrink-0 transition-transform ${expanded ? 'rotate-90' : ''}`} />
                          <span className="flex-1 truncate">{cat}</span>
                          <span className="text-[10px] text-gray-400">{count}</span>
                        </button>
                        {expanded && subCats && (
                          <div className="pl-5 border-l-2 border-gray-100 ml-2 mt-1 space-y-0.5">
                            {subCats.map(sub => {
                              const subCount = subCategoryCounts[sub] || 0
                              const isSubActive = selectedSubCategory === sub
                              return (
                                <button
                                  key={sub}
                                  onClick={() => setSelectedSubCategory(isSubActive ? null : sub)}
                                  className={`w-full text-left px-3 py-1.5 rounded-md text-xs transition-colors ${
                                    isSubActive
                                      ? 'bg-blue-50 text-gcb-primary font-medium'
                                      : 'text-gray-500 hover:bg-gray-50'
                                  }`}
                                >
                                  {sub}
                                  <span className="ml-auto text-[10px] text-gray-400">{subCount}</span>
                                </button>
                              )
                            })}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            </ScrollArea>
          </div>

          {/* 右侧 */}
          <div className="flex-1 min-w-0">
            <Card className="mb-4">
              <CardContent className="p-3">
                <div className="flex items-center gap-2">
                  <Search className="w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="搜索主题名称、描述、部门..."
                    value={searchKeyword}
                    onChange={(e) => setSearchKeyword(e.target.value)}
                    className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0 px-0 text-sm"
                  />
                  {searchKeyword && (
                    <Button variant="ghost" size="sm" onClick={() => setSearchKeyword('')} className="h-6 px-2 text-xs">
                      清空
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3 border-b">
                <div className="flex items-center justify-between">
                  <h2 className="font-semibold text-gray-900 text-sm">
                    {showFavoritesOnly ? '我收藏的主题' : selectedCategory ? `${selectedCategory}主题` : '全部业务主题'}
                  </h2>
                  <span className="text-xs text-gray-500">{displayTopics.length} 个主题</span>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="h-[500px]">
                  <div className="divide-y divide-gray-100">
                    {displayTopics.length === 0 ? (
                      <div className="text-center py-12 text-gray-500 text-sm">
                        {searchKeyword ? '未找到匹配的主题' : showFavoritesOnly ? '暂无收藏主题' : '该目录下暂无主题'}
                      </div>
                    ) : (
                      displayTopics.map((topic) => (
                        <div
                          key={topic.id}
                          className="p-4 hover:bg-blue-50/30 transition-colors group"
                        >
                          <div className="flex gap-3">
                            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-100 to-blue-50 flex items-center justify-center shrink-0">
                              <Database className="w-5 h-5 text-gcb-primary" />
                            </div>
                            <div className="flex-1 min-w-0 cursor-pointer" onClick={() => onLearnMore(topic.catalogId)}>
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-medium text-sm text-gray-900 group-hover:text-gcb-primary transition-colors">
                                  {topic.name}
                                </h3>
                                <span className="text-[10px] text-gray-400">{topic.department}</span>
                              </div>
                              <p className="text-xs text-gray-500 mb-2">{topic.description}</p>
                              <div className="flex items-center gap-4 text-xs text-gray-400">
                                <span className="flex items-center gap-1">
                                  <TableIcon className="w-3 h-3" />
                                  {topic.tableCount} 张表
                                </span>
                                <span className="flex items-center gap-1">
                                  <TrendingUp className="w-3 h-3" />
                                  {topic.recordCount}
                                </span>
                                <span className="flex items-center gap-1">
                                  <RefreshCw className="w-3 h-3" />
                                  {topic.updateFrequency}
                                </span>
                              </div>
                            </div>
                            <div className="shrink-0 flex flex-col items-end justify-between self-stretch">
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    toggleFavorite(topic.id)
                                  }}
                                  className="transition-colors shrink-0 p-1 rounded hover:bg-gray-100"
                                  title={favorites.has(topic.id) ? '取消收藏' : '收藏此主题'}
                                >
                                  <Star className={`w-4 h-4 ${favorites.has(topic.id) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300 group-hover:text-gray-400'}`} />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    onLearnMore(topic.catalogId)
                                  }}
                                  className="flex items-center gap-1 text-xs text-gcb-primary hover:text-gcb-primaryDark transition-colors shrink-0"
                                >
                                  了解更多 <ArrowRight className="w-3 h-3" />
                                </button>
                              </div>
                              <div className="flex items-center gap-1.5">
                                {version !== '1' && (
                                  <Button
                                    size="sm"
                                    className="h-7 text-xs bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white shadow-sm"
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      openAiDialog(topic.id)
                                    }}
                                  >
                                    <Sparkles className="w-3.5 h-3.5 mr-1" /> AI问数
                                  </Button>
                                )}
                                {onSelfAnalysis && (
                                  <Button
                                    size="sm"
                                    className="h-7 text-xs bg-gcb-primary hover:bg-gcb-primaryDark text-white"
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      onSelfAnalysis(topic.id)
                                    }}
                                  >
                                    <Zap className="w-3 h-3 mr-1" /> 自主分析
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* AI问数 Dialog — v2.0 only */}
      {version !== '1' && (
      <Dialog open={showAiDialog} onOpenChange={setShowAiDialog}>
        <DialogContent className="sm:max-w-[700px] w-[95vw] h-[90vh] max-h-[90vh] flex flex-col p-0 gap-0">
          {/* 标题 */}
          <div className="px-6 py-4 border-b shrink-0">
            <DialogTitle className="flex items-center gap-2 text-base">
              <Sparkles className="w-5 h-5 text-violet-500" />
              AI问数 — {aiTopicName}
            </DialogTitle>
          </div>

          {/* 对话内容区 — 可滚动 */}
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4 min-h-0">
            {/* 初始提示 */}
            {aiHistory.length === 0 && !aiLoading && (
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shrink-0">
                  <Sparkles className="w-4 h-4 text-white" />
                </div>
                <div className="bg-gray-50 rounded-2xl rounded-tl-md px-4 py-3 max-w-sm">
                  <p className="text-sm text-gray-700">您好！我是 AI 数据助手，可以帮您分析 <strong>{aiTopicName}</strong> 相关数据。</p>
                  <p className="text-sm text-gray-700 mt-1">请选择下方推荐问题，或自行输入查询，可连续提问。</p>
                </div>
              </div>
            )}

            {/* 历史对话 */}
            {aiHistory.map((item, idx) => (
              <div key={idx} className="space-y-3">
                {/* 用户问题 */}
                <div className="flex justify-end">
                  <div className="bg-violet-600 text-white rounded-2xl rounded-tr-md px-4 py-2.5 max-w-md">
                    <p className="text-sm">{item.query}</p>
                  </div>
                </div>

                {/* AI回复 */}
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shrink-0">
                    <Sparkles className="w-4 h-4 text-white" />
                  </div>
                  <div className="flex-1 min-w-0 border rounded-lg overflow-hidden bg-white shadow-sm">
                    {/* 标题 */}
                    <div className="bg-violet-50 px-4 py-2.5 border-b">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {item.resultType === 'chart' ? <TrendingUp className="w-4 h-4 text-violet-600" /> : <TableIcon className="w-4 h-4 text-violet-600" />}
                          <h4 className="text-sm font-semibold text-gray-800">{item.title}</h4>
                        </div>
                        <Badge variant="outline" className="text-[10px] h-5 px-1.5 bg-white text-violet-600 border-violet-200">
                          {item.resultType === 'chart' ? '图表' : '表格'}
                        </Badge>
                      </div>
                      {item.summary && <p className="text-xs text-gray-500 mt-1">{item.summary}</p>}
                    </div>

                    {/* 图表 */}
                    {item.resultType === 'chart' && <ChartVisualization result={item} />}

                    {/* 表格 */}
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="bg-gray-50 border-b">
                            {item.columns.map(col => (
                              <th key={col.key} className="px-3 py-2 text-left font-medium text-gray-500 whitespace-nowrap">{col.label}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {item.rows.map((row, ri) => (
                            <tr key={ri} className="border-b last:border-0 hover:bg-gray-50/50">
                              {item.columns.map(col => (
                                <td key={col.key} className="px-3 py-2 text-gray-700 whitespace-nowrap">{row[col.key] as string | number}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* 下载 */}
                    <div className="px-3 py-2 bg-gray-50 border-t flex justify-end">
                      <Button size="sm" variant="outline" onClick={() => handleDownloadResult(item)} className="text-xs h-7">
                        <Download className="w-3.5 h-3.5 mr-1" /> 下载结果
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* 加载中 */}
            {aiLoading && (
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shrink-0">
                  <Sparkles className="w-4 h-4 text-white" />
                </div>
                <div className="bg-gray-50 rounded-2xl rounded-tl-md px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-violet-500" />
                    <p className="text-sm text-gray-500">AI正在分析您的问题...</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* 推荐问数 — 固定区域 */}
          <div className="border-t bg-gray-50/60 px-6 py-3 shrink-0">
            <h4 className="text-xs font-semibold text-gray-500 mb-2 flex items-center gap-1.5">
              <TrendingUp className="w-3.5 h-3.5" /> 推荐问数 <span className="text-[10px] text-gray-400 font-normal">（点击可继续提问）</span>
            </h4>
            <div className="space-y-1.5">
              {getTopicQueries(aiTopicId).map(q => (
                <button
                  key={q.id}
                  onClick={() => handleSelectQuery(q.id, q.query)}
                  disabled={aiLoading}
                  className={cn(
                    'w-full text-left p-2.5 rounded-lg border transition-all text-sm disabled:opacity-50',
                    aiSelectedQueryId === q.id
                      ? 'border-violet-300 bg-violet-50 ring-1 ring-violet-200'
                      : 'border-gray-200 hover:border-violet-200 hover:bg-white'
                  )}
                >
                  <div className="flex items-start gap-2">
                    <Sparkles className="w-3.5 h-3.5 text-violet-500 mt-0.5 shrink-0" />
                    <div>
                      <p className="font-medium text-gray-800">{q.query}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{q.description}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* 输入栏 */}
          <div className="border-t bg-white px-6 py-3 shrink-0">
            <div className="flex items-center gap-2">
              <Input
                placeholder="输入问题，按回车发送..."
                value={aiQueryInput}
                onChange={(e) => setAiQueryInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && aiQueryInput.trim() && !aiLoading) runAiQuery() }}
                className="text-sm"
              />
              <Button size="sm" disabled={!aiQueryInput.trim() || aiLoading} onClick={() => runAiQuery()} className="bg-violet-600 hover:bg-violet-700 text-white shrink-0 h-9">
                {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              </Button>
            </div>
          </div>

          {/* 底部 */}
          <div className="border-t px-6 py-2 flex justify-end shrink-0">
            <Button variant="outline" size="sm" onClick={() => setShowAiDialog(false)}>关闭</Button>
          </div>
        </DialogContent>
      </Dialog>
      )}
    </div>
  )
}
